import React, { useState, useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import { dismissPopupForSession } from './AutoPopup';

// Icon components for benefits
const BenefitIcon = ({ type }) => {
    const icons = {
        gift: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
        ),
        dollar: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        star: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
        ),
        heart: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
        sparkles: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
        ),
        shield: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
        crown: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l3.5 7L12 6l3.5 4L19 3M5 21h14M5 17h14" />
            </svg>
        ),
        check: (
            <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
        ),
    };
    return icons[type] || icons.gift;
};

// Join Rituals Modal Component - CMS Powered
export default function JoinRitualsModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        email: '',
        name: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalData, setModalData] = useState(null);

    useEffect(() => {
        // Fetch modal content from CMS
        fetch('/api/content/join-modal/')
            .then(res => res.ok ? res.json() : null)
            .then(data => data && setModalData(data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Use API data or defaults
    const logoImage = modalData?.logo_image || '/static/M_Emblem.webp';
    const title = modalData?.title || 'Join Our Rituals';
    const subtitle = modalData?.subtitle || 'Be among the first to experience MELANUA™. Receive exclusive benefits and early access.';
    const nameLabel = modalData?.name_label || 'Your Name';
    const namePlaceholder = modalData?.name_placeholder || 'Enter your name';
    const emailLabel = modalData?.email_label || 'Email Address';
    const emailPlaceholder = modalData?.email_placeholder || 'your@email.com';
    const submitButtonText = modalData?.submit_button_text || 'Join the Ritual';
    const submittingText = modalData?.submitting_text || 'Joining...';
    const successMessage = modalData?.success_message || 'Welcome to MELANUA™! Check your email for exclusive early access benefits.';
    const privacyNote = modalData?.privacy_note || 'By joining, you agree to receive updates from MELANUA™. We respect your privacy and you can unsubscribe anytime.';
    const benefits = modalData?.benefits || [
        { icon: 'gift', text: 'Early Access' },
        { icon: 'dollar', text: 'Special Pricing' },
        { icon: 'star', text: 'VIP Benefits' },
    ];

    // Discount offer settings
    const showDiscountOffer = modalData?.show_discount_offer || false;
    const discountHeadline = modalData?.discount_headline || '';
    const discountDescription = modalData?.discount_description || '';
    const discountCode = modalData?.discount_code || '';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;

        try {
            const response = await fetch('/api/waitlist/submit/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setStatus({
                    type: 'success',
                    message: successMessage
                });
                setFormData({ email: '', name: '' });

                trackEvent('Waitlist', 'Submit', 'Join Rituals Modal');

                // Dismiss popup for the rest of the session after successful subscription
                dismissPopupForSession();

                setTimeout(() => {
                    onClose();
                    setStatus({ type: '', message: '' });
                }, discountCode ? 6000 : 3000); // Wait longer if showing discount code
            } else {
                setStatus({
                    type: 'error',
                    message: data.error || 'Something went wrong. Please try again.'
                });
            }
        } catch (error) {
            setStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const overlayStyle = {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        backgroundColor: 'rgba(45, 45, 45, 0.85)',
        zIndex: '999999',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto'
    };

    const modalStyle = {
        position: 'relative',
        width: '100%',
        maxWidth: '600px',
        backgroundColor: '#FFFFFF',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxHeight: '90vh',
        overflowY: 'auto',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F0E9 100%)'
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(216, 194, 157, 0.2)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        zIndex: '10'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(216, 194, 157, 0.4)';
                        e.target.style.transform = 'rotate(90deg)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(216, 194, 157, 0.2)';
                        e.target.style.transform = 'rotate(0deg)';
                    }}
                >
                    <svg style={{width: '20px', height: '20px', color: '#2D2D2D'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Modal Content */}
                <div style={{padding: '48px'}}>
                    {/* Header */}
                    <div style={{textAlign: 'center', marginBottom: '32px'}}>
                        <div style={{width: '64px', height: '64px', margin: '0 auto 16px', borderRadius: '50%', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
                            <img src={logoImage} alt="MELANUA™" width="64" height="64" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                        </div>
                        <h2 style={{fontSize: '40px', fontFamily: 'Cormorant Garamond, serif', fontWeight: 'bold', color: '#2D2D2D', marginBottom: '12px'}}>
                            {title}
                        </h2>
                        <p style={{fontSize: '18px', color: 'rgba(45, 45, 45, 0.7)', maxWidth: '448px', margin: '0 auto'}}>
                            {subtitle}
                        </p>
                    </div>

                    {/* Discount Offer Banner - shows when discount is enabled */}
                    {showDiscountOffer && discountHeadline && (
                        <div style={{
                            marginBottom: '24px',
                            padding: '20px',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(174, 133, 43, 0.15) 0%, rgba(216, 194, 157, 0.2) 100%)',
                            border: '2px solid rgba(174, 133, 43, 0.3)',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                margin: '0 auto 12px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(174, 133, 43, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg style={{width: '24px', height: '24px', color: '#ae852b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                            <h3 style={{
                                fontSize: '22px',
                                fontFamily: 'Cormorant Garamond, serif',
                                fontWeight: 'bold',
                                color: '#ae852b',
                                marginBottom: '8px'
                            }}>
                                {discountHeadline}
                            </h3>
                            {discountDescription && (
                                <p style={{fontSize: '14px', color: 'rgba(45, 45, 45, 0.8)', lineHeight: '1.5'}}>
                                    {discountDescription}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Benefits Grid */}
                    <div style={{display: 'grid', gridTemplateColumns: `repeat(${Math.min(benefits.length, 3)}, 1fr)`, gap: '16px', marginBottom: '32px'}}>
                        {benefits.map((benefit, index) => (
                            <div key={benefit.id || index} style={{textAlign: 'center', padding: '16px', borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                                <div style={{width: '48px', height: '48px', margin: '0 auto 8px', borderRadius: '50%', backgroundColor: 'rgba(216, 194, 157, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <BenefitIcon type={benefit.icon} />
                                </div>
                                <p style={{fontSize: '14px', fontWeight: '500', color: '#2D2D2D'}}>{benefit.text}</p>
                            </div>
                        ))}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                        <div>
                            <label htmlFor="modal-name" style={{display: 'block', color: '#2D2D2D', fontWeight: '500', marginBottom: '8px'}}>
                                {nameLabel}
                            </label>
                            <input
                                type="text"
                                id="modal-name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="luxury-input"
                                placeholder={namePlaceholder}
                            />
                        </div>

                        <div>
                            <label htmlFor="modal-email" style={{display: 'block', color: '#2D2D2D', fontWeight: '500', marginBottom: '8px'}}>
                                {emailLabel}
                            </label>
                            <input
                                type="email"
                                id="modal-email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="luxury-input"
                                placeholder={emailPlaceholder}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-primary"
                            style={{width: '100%', padding: '16px'}}
                        >
                            {isSubmitting ? (
                                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <svg style={{animation: 'spin 1s linear infinite', marginRight: '12px', height: '20px', width: '20px'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle style={{opacity: 0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path style={{opacity: 0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {submittingText}
                                </span>
                            ) : (
                                submitButtonText
                            )}
                        </button>

                        {status.message && (
                            <div style={{
                                padding: '16px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                fontWeight: '500',
                                backgroundColor: status.type === 'success' ? 'rgba(184, 193, 177, 0.2)' : 'rgba(200, 165, 165, 0.2)',
                                color: status.type === 'success' ? '#4a5d4a' : '#C8A5A5',
                                border: status.type === 'success' ? '2px solid rgba(184, 193, 177, 0.3)' : '2px solid rgba(200, 165, 165, 0.3)'
                            }}>
                                {status.message}
                                {/* Show discount code after successful subscription */}
                                {status.type === 'success' && discountCode && (
                                    <div style={{marginTop: '12px', padding: '12px', backgroundColor: 'rgba(174, 133, 43, 0.1)', borderRadius: '8px', border: '2px dashed #ae852b'}}>
                                        <p style={{fontSize: '12px', color: '#ae852b', marginBottom: '4px'}}>Your exclusive discount code:</p>
                                        <p style={{fontSize: '20px', fontWeight: 'bold', color: '#ae852b', letterSpacing: '2px'}}>{discountCode}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </form>

                    {/* Privacy Note */}
                    <p style={{fontSize: '12px', color: 'rgba(45, 45, 45, 0.6)', textAlign: 'center', marginTop: '24px', lineHeight: '1.5'}}>
                        {privacyNote.split('\n').map((line, idx) => (
                            <React.Fragment key={idx}>
                                {line}
                                {idx < privacyNote.split('\n').length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
